import { useLayoutEffect, useRef, useState } from 'react';
import { FiChevronDown, FiChevronUp, FiFileText, FiPlus, FiSlash, FiTrash2, FiUser } from 'react-icons/fi';
import { mergeTermRows } from './quoteCatalog';
import '../styles/QuotationForm.css';

function QuoteTermBody({ value, onChange, onBlur }) {
  const ref = useRef(null);
  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = '0px';
    node.style.height = `${node.scrollHeight}px`;
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      onBlur={onBlur}
    />
  );
}

function quoteTermRows(rows, section, blank) {
  const found = (rows || []).filter((row) => row.section === section);
  return found.length ? found : [blank];
}

/** Same terms editor the quotation uses. Matching wording stays once. */
export default function QuoteTermsEditor({ rows, onChange }) {
  const list = Array.isArray(rows) ? rows : [];
  const [notesOpen, setNotesOpen] = useState(true);
  const setRows = (next) => onChange(typeof next === 'function' ? next(list) : next);

  const collapseTerms = () => setRows((current) => mergeTermRows(current, []));

  const patchQuoteTerm = (key, patch) => {
    setRows((current) => {
      const exists = current.some((row) => row.key === key);
      if (exists) return current.map((row) => (row.key === key ? { ...row, ...patch } : row));
      return [...current, {
        key,
        section: patch.section || 'serviceTerms',
        title: '',
        body: '',
        text: '',
        selected: true,
        custom: true,
        sources: [],
        ...patch,
      }];
    });
  };

  const addQuoteTerm = (section) => {
    setRows((current) => [
      ...current,
      {
        key: `custom-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        section,
        title: '',
        body: '',
        text: '',
        selected: true,
        custom: true,
        sources: [],
      },
    ]);
  };

  const removeQuoteTerm = (key) => {
    setRows((current) => current.filter((row) => row.key !== key));
  };

  return (
    <section className="qf-terms">
      <header className="qf-terms-head">
        <div>
          <h3>Terms</h3>
          <p>The same wording from more than one service is kept once. Different wording is added as its own line.</p>
        </div>
      </header>

      <article>
        <header>
          <i><FiFileText /></i>
          <div>
            <h3>Service Terms</h3>
            <p>General terms included with the selected services.</p>
          </div>
        </header>
        <div className="qf-term-table">
          {quoteTermRows(list, 'serviceTerms', {
            key: 'draft-serviceTerms', section: 'serviceTerms', title: '', body: '', text: '',
          }).map((row) => {
            const filled = Boolean(String(row.title || '').trim() || String(row.body || '').trim());
            return (
              <div className={`qf-term-pair${filled ? ' is-filled' : ''}`} key={row.key}>
                <input
                  value={row.title || ''}
                  onChange={(e) => patchQuoteTerm(row.key, { section: 'serviceTerms', title: e.target.value })}
                  onBlur={collapseTerms}
                />
                <QuoteTermBody
                  value={row.body || ''}
                  onChange={(body) => patchQuoteTerm(row.key, { section: 'serviceTerms', body })}
                  onBlur={collapseTerms}
                />
                {filled ? (
                  <button type="button" aria-label="Remove term" onClick={() => removeQuoteTerm(row.key)}>
                    <FiTrash2 />
                  </button>
                ) : <span />}
              </div>
            );
          })}
        </div>
        <button type="button" className="qf-term-add" onClick={() => addQuoteTerm('serviceTerms')}>
          <FiPlus /> Add Service term
        </button>
      </article>

      {[
        ['responsibilities', 'Client Responsibilities', 'What the client needs to provide or disclose before and during service.', 'Add Responsibility', <FiUser />],
        ['exclusions', 'Service Exclusions', 'What is not covered under the selected services.', 'Add Exclusion', <FiSlash />],
      ].map(([section, title, hint, addLabel, icon]) => (
        <article key={section}>
          <header>
            <i>{icon}</i>
            <div>
              <h3>{title}</h3>
              <p>{hint}</p>
            </div>
          </header>
          {quoteTermRows(list, section, {
            key: `draft-${section}`, section, title: '', body: '', text: '',
          }).map((row) => (
            <div className="qf-term-line" key={row.key}>
              <b />
              <input
                value={row.text || ''}
                onChange={(e) => patchQuoteTerm(row.key, { section, text: e.target.value })}
                onBlur={collapseTerms}
              />
              <button type="button" aria-label={`Remove ${title}`} onClick={() => removeQuoteTerm(row.key)}>
                <FiTrash2 />
              </button>
            </div>
          ))}
          <button type="button" className="qf-term-add" onClick={() => addQuoteTerm(section)}>
            <FiPlus /> {addLabel}
          </button>
        </article>
      ))}

      <article>
        <header>
          <i><FiFileText /></i>
          <div>
            <h3>Scope-Specific Notes <em>optional</em></h3>
            <p>Notes from the selected services. The same note is kept once.</p>
          </div>
          <button type="button" className="qf-term-fold" onClick={() => setNotesOpen((open) => !open)} aria-label="Toggle notes">
            {notesOpen ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </header>
        {notesOpen ? (
          <>
            {list.filter((row) => row.section === 'notes').map((row) => (
              <div className="qf-term-line" key={row.key}>
                <b />
                <input
                  value={row.text || ''}
                  onChange={(e) => patchQuoteTerm(row.key, { section: 'notes', text: e.target.value })}
                  onBlur={collapseTerms}
                />
                <button type="button" aria-label="Remove note" onClick={() => removeQuoteTerm(row.key)}>
                  <FiTrash2 />
                </button>
              </div>
            ))}
            <button type="button" className="qf-term-add" onClick={() => addQuoteTerm('notes')}>
              <FiPlus /> Add Note
            </button>
          </>
        ) : null}
      </article>
    </section>
  );
}
